import { useEffect, useRef } from 'react';
import type { ValidationResult } from '@/features/line-items/line-item-form/common/selectors';

export type ErrorFieldIdResolver = (key: string) => string | undefined;

const DEFAULT_FIELD_ID_MAP: Readonly<Record<string, string>> = {
  name: 'line-item-name',
  price: 'line-item-price',
  media_budget: 'line-item-media-budget',
  purchase_order_number: 'line-item-purchase-order-number',
  channel_id: 'line-item-channel',
  tactic_id: 'line-item-tactic',
  unit_price_type_id: 'line-item-unit-price-type',
  unit_price: 'line-item-unit-price',
  estimated_units: 'line-item-estimated-units',
  dates: 'line-item-dates',
  planned_unit_cost: 'line-item-planned-unit-cost',
  media_platform_ids: 'line-item-media-platforms'
};

export function useScrollToFirstError(
  errors: ValidationResult,
  submitAttempted: boolean,
  resolveId?: ErrorFieldIdResolver
): void {
  const lastScrolledKey = useRef<string | null>(null);
  const lastAttempt = useRef(false);
  useEffect(() => {
    if (!submitAttempted) {
      lastAttempt.current = false;
      lastScrolledKey.current = null;
      return;
    }
    const firstKey = pickFirstErrorKey(errors);
    const becameAttempted = !lastAttempt.current;
    lastAttempt.current = true;
    if (firstKey === undefined) return;
    if (!becameAttempted && lastScrolledKey.current === firstKey) return;
    lastScrolledKey.current = firstKey;

    const element = locateErrorElement(firstKey, resolveId);
    if (!element) return;
    element.scrollIntoView({ behavior: 'smooth', block: 'center' });
    if (typeof element.focus === 'function') {
      element.focus({ preventScroll: true });
    }
  }, [errors, submitAttempted, resolveId]);
}

function pickFirstErrorKey(errors: ValidationResult): string | undefined {
  const keys = Object.keys(errors.fieldErrors);
  if (keys.length === 0) return errors.formErrors.length > 0 ? '__form' : undefined;
  return keys[0];
}

function locateErrorElement(key: string, resolveId?: ErrorFieldIdResolver): HTMLElement | null {
  if (key === '__form') return null;
  const resolvedId = resolveId?.(key) ?? DEFAULT_FIELD_ID_MAP[key];
  if (resolvedId) {
    const byId = document.getElementById(resolvedId);
    if (byId) return byId as HTMLElement;
  }
  const byDataAttr = document.querySelector<HTMLElement>(`[data-error-id="${cssEscape(key)}"]`);
  if (byDataAttr) return byDataAttr;
  return null;
}

function cssEscape(value: string): string {
  if (
    typeof (globalThis as { CSS?: { escape?: (s: string) => string } }).CSS?.escape === 'function'
  ) {
    return (globalThis as { CSS: { escape: (s: string) => string } }).CSS.escape(value);
  }
  return value.replace(/(["\\])/g, '\\$1');
}
